import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Calendar,
  CalendarDays,
  Clock,
  Edit,
  ExternalLink,
  FileText,
  Mail,
  MapPin,
  Package,
  Phone,
  Plus,
  Trash2,
  User,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { EditMeetingDialog } from "@/components/meetings/EditMeetingDialog";
import { MeetingList } from "@/components/meetings/MeetingList";
import { ScheduleMeetingDialog } from "@/components/meetings/ScheduleMeetingDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  useCustomerMeetings,
  useDeleteMeeting,
  useUpdateMeetingStatus,
} from "@/hooks/useCustomerMeetings";
import cloudflare from "@/integrations/cloudflare/client";
import type { Meeting } from "@/types/meeting.types";

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface Project {
  id: number;
  email: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  budget?: number;
  timeframe?: string;
  status: string;
  notes?: string;
  created_at: string;
}

interface CustomerViewProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  customer: Customer | null;
}

export function CustomerView({ isOpen, onClose, onEdit, onDelete, customer }: CustomerViewProps) {
  const navigate = useNavigate();
  const [isSchedulingMeeting, setIsSchedulingMeeting] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);

  // Fetch customer's projects
  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ["customer-projects", customer?.id],
    queryFn: async () => {
      if (!customer) {
        return [];
      }
      const response = await fetch(`${cloudflare.apiUrl}/api/v1/customers/${customer.id}/projects`);
      if (!response.ok) {
        throw new Error("Failed to fetch projects");
      }
      const result = await response.json();
      return result.data as Project[];
    },
    enabled: !!customer,
  });

  // Fetch customer's meetings
  const { data: meetings = [], isLoading: meetingsLoading } = useCustomerMeetings(
    customer?.id || 0
  );

  // Meeting mutations
  const deleteMeeting = useDeleteMeeting();
  const updateMeetingStatus = useUpdateMeetingStatus();

  const handleDeleteMeeting = async (meeting: Meeting) => {
    if (!confirm("Are you sure you want to delete this meeting?")) {
      return;
    }
    await deleteMeeting.mutateAsync({
      meetingId: meeting.id,
      customerId: meeting.customer_id,
    });
  };

  const handleCompleteMeeting = async (meeting: Meeting) => {
    await updateMeetingStatus.mutateAsync({
      meetingId: meeting.id,
      status: "completed",
    });
  };

  const handleCancelMeeting = async (meeting: Meeting) => {
    if (!confirm("Are you sure you want to cancel this meeting?")) {
      return;
    }
    await updateMeetingStatus.mutateAsync({
      meetingId: meeting.id,
      status: "cancelled",
    });
  };

  if (!customer) {
    return null;
  }

  const formatAddress = () => {
    const parts = [];
    if (customer.address) {
      parts.push(customer.address);
    }
    if (customer.city) {
      parts.push(customer.city);
    }
    if (customer.state) {
      parts.push(customer.state);
    }
    if (customer.zip_code) {
      parts.push(customer.zip_code);
    }
    if (customer.country && customer.country !== "USA") {
      parts.push(customer.country);
    }
    return parts.join(", ");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-4 pr-12">
          <div className="flex justify-between items-start gap-4">
            <DialogTitle className="text-2xl flex-1">Customer Details</DialogTitle>
            <div className="flex gap-2 flex-shrink-0">
              <Button onClick={onEdit} size="sm" variant="outline">
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
              <Button onClick={onDelete} size="sm" variant="destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Customer Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {customer.first_name} {customer.last_name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Contact Information */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${customer.email}`} className="text-blue-600 hover:underline">
                    {customer.email}
                  </a>
                </div>

                {customer.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${customer.phone}`} className="text-blue-600 hover:underline">
                      {customer.phone}
                    </a>
                  </div>
                )}

                {formatAddress() && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span className="text-sm">{formatAddress()}</span>
                  </div>
                )}
              </div>

              <Separator />

              {/* Metadata */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>
                    Created on{" "}
                    {new Date(customer.created_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>
                    Last updated{" "}
                    {new Date(customer.updated_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
              </div>

              {customer.notes && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      Notes
                    </div>
                    <p className="text-sm text-muted-foreground pl-6">{customer.notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Projects Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Projects ({projects?.length || 0})
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() => {
                    onClose();
                    navigate(`/new-project?customer_id=${customer.id}`);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create New Project
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {projectsLoading ? (
                <div className="text-center py-4 text-muted-foreground">Loading projects...</div>
              ) : projects && projects.length > 0 ? (
                <div className="space-y-3">
                  {projects.map((project) => (
                    <button
                      key={project.id}
                      type="button"
                      className="w-full text-left border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer group"
                      onClick={() => {
                        onClose();
                        navigate(`/projects/${project.id}`);
                      }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            Project #{project.id}
                            <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Created {new Date(project.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(project.status)}>{project.status}</Badge>
                          <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {project.budget && (
                          <div>
                            <span className="text-muted-foreground">Budget:</span> $
                            {project.budget.toLocaleString()}
                          </div>
                        )}
                        {project.timeframe && (
                          <div>
                            <span className="text-muted-foreground">Timeframe:</span>{" "}
                            {project.timeframe}
                          </div>
                        )}
                      </div>

                      {project.notes && (
                        <div className="mt-2 text-sm text-muted-foreground">{project.notes}</div>
                      )}

                      <div className="mt-3 text-sm text-blue-600 group-hover:text-blue-700 font-medium">
                        View Project Details →
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  No projects found for this customer
                </div>
              )}
            </CardContent>
          </Card>

          {/* Meetings Section */}
          <MeetingList
            meetings={meetings}
            isLoading={meetingsLoading}
            onSchedule={() => setIsSchedulingMeeting(true)}
            onEdit={(meeting) => setEditingMeeting(meeting)}
            onDelete={handleDeleteMeeting}
            onComplete={handleCompleteMeeting}
            onCancel={handleCancelMeeting}
          />
        </div>

        {/* Meeting Dialogs */}
        <ScheduleMeetingDialog
          isOpen={isSchedulingMeeting}
          onClose={() => setIsSchedulingMeeting(false)}
          customerId={customer.id}
        />
        <EditMeetingDialog
          isOpen={!!editingMeeting}
          onClose={() => setEditingMeeting(null)}
          meeting={editingMeeting}
        />
      </DialogContent>
    </Dialog>
  );
}
